import { NavLink, Link } from 'react-router-dom';
import { LOCALES, LOCALE_NAMES, useI18n, type Locale } from '../i18n';
import { useTheme, type ThemePreference } from '../lib/theme';
import { useAuth } from '../lib/auth';
import { QuotaBadge } from './QuotaBadge';

/**
 * The one persistent chrome in the application: identity, navigation, quota,
 * and the two viewer preferences. Kept to a single 52px row so the result
 * screen loses as little vertical space as possible.
 */
export function AppHeader({ compact = false }: { compact?: boolean }) {
  const { t, locale, setLocale } = useI18n();
  const { preference, setPreference } = useTheme();
  const { status, user, signIn, signOut, error } = useAuth();

  return (
    <header className="header">
      <Link className="header__brand" to="/">
        {/* Read from the locale, not hard-coded: the product name appears in
            the title, the meta description and here, and a copy that does not
            follow the others is exactly how a rename half-lands. */}
        <span className="header__mark">{t('app.name')}</span>
        {compact ? null : <span className="header__subtitle">{t('app.subtitle')}</span>}
      </Link>

      <nav className="header__nav" aria-label={t('app.name')}>
        <NavLink className="navlink" to="/" end>
          {t('nav.new')}
        </NavLink>
        <NavLink className="navlink" to="/maps">
          {t('nav.saved')}
        </NavLink>
      </nav>

      <span className="header__spacer" />

      <div className="header__right">
        <QuotaBadge />

        <label className="visually-hidden" htmlFor="theme-select">
          {t('header.themeLabel')}
        </label>
        <select
          id="theme-select"
          className="select"
          style={{ width: 'auto' }}
          value={preference}
          onChange={(event) => setPreference(event.target.value as ThemePreference)}
        >
          <option value="system">{t('header.theme.system')}</option>
          <option value="light">{t('header.theme.light')}</option>
          <option value="dark">{t('header.theme.dark')}</option>
        </select>

        <label className="visually-hidden" htmlFor="locale-select">
          {t('header.localeLabel')}
        </label>
        <select
          id="locale-select"
          className="select"
          style={{ width: 'auto' }}
          value={locale}
          onChange={(event) => setLocale(event.target.value as Locale)}
        >
          {LOCALES.map((code) => (
            <option key={code} value={code}>
              {LOCALE_NAMES[code]}
            </option>
          ))}
        </select>

        {status === 'signed-in' && user ? (
          <>
            <span className="user-chip">
              {user.photoURL ? (
                <img className="user-chip__avatar" src={user.photoURL} alt="" width={24} height={24} />
              ) : null}
              <span>{user.displayName ?? user.email ?? ''}</span>
            </span>
            <button type="button" className="button button--quiet" onClick={() => void signOut()}>
              {t('header.signOut')}
            </button>
          </>
        ) : null}

        {status === 'signed-out' ? (
          <button type="button" className="button" onClick={() => void signIn()}>
            {t('header.signIn')}
          </button>
        ) : null}

        {status === 'loading' ? <span className="faint">{t('header.signingIn')}</span> : null}
      </div>

      {error ? (
        <span className="visually-hidden" role="status">
          {t('header.signInFailed', { message: error })}
        </span>
      ) : null}
    </header>
  );
}
